<template>
  <v-card outlined class="pa-4 petmed-services-chart">
    <div class="d-flex align-center justify-space-between flex-wrap mb-3 header">
      <span class="pw-section">Servicios consumidos</span>
      <v-btn-toggle v-if="showClinicServices" v-model="mode" mandatory dense color="primary" class="mode-toggle">
        <v-btn value="pawer" small>Servicio Pawer</v-btn>
        <v-btn value="clinic" small>Servicio de clínica</v-btn>
      </v-btn-toggle>
    </div>

    <div v-if="loading" class="state-msg py-10 text-center">
      <v-progress-circular indeterminate size="24" width="2" color="primary" />
    </div>
    <div v-else-if="error" class="state-msg py-10 text-center">
      <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
      No se pudo cargar el ranking de servicios.
    </div>
    <div v-else-if="rows.length === 0" class="state-msg py-10 text-center">
      Sin servicios en el período
    </div>
    <div v-else class="chart-container" :style="{ height: `${chartHeight}px` }">
      <horizontal-bar-chart :chart-data="chartData" :options="chartOptions" :styles="chartStyles" />
    </div>
  </v-card>
</template>

<script>
import { HorizontalBar } from 'vue-chartjs';
import { topWithOthers } from '@/utils/petmed-metrics.utils';

// Chart.js pinta en canvas y no lee custom properties: se repiten aquí los
// valores de la paleta de src/styles/variables.scss.
const COLORS = {
  primary: '#104B94',
  textMuted: '#6B7280',
  divider: '#F3F4F6',
};

const TOP_N = 10;
const ROW_HEIGHT = 30;
const MAX_LABEL = 28;

const clpFormatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });

// Wrapper de vue-chartjs (mismo patrón que B2CGrowthChart): re-renderiza al cambiar los datos.
const HorizontalBarChart = {
  extends: HorizontalBar,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) this.$data._chart.destroy();
        this.renderChart(newData, this.options);
      },
    },
  },
};

export default {
  name: 'PetmedServicesChart',
  components: { HorizontalBarChart },

  props: {
    // insights.byPawerService / insights.byClinicService del contrato
    byPawerService: { type: Array, default: () => [] },
    byClinicService: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    // Sin `petmed-metrics:view-costs` el tooltip no muestra la cobertura.
    showCosts: { type: Boolean, default: true },
    // Sin `petmed-metrics:view-clinics` no llegan los servicios propios de cada clínica.
    showClinicServices: { type: Boolean, default: true },
  },

  data() {
    return {
      mode: 'pawer',
      chartStyles: { height: '100%', position: 'relative' },
    };
  },

  computed: {
    countKey() {
      return this.mode === 'pawer' ? 'usageCount' : 'attendanceCount';
    },
    rows() {
      const source = this.mode === 'pawer' ? this.byPawerService : this.byClinicService;
      return topWithOthers(source, TOP_N, this.countKey, ['pawerCoverage']);
    },
    chartHeight() {
      return this.rows.length * ROW_HEIGHT + 40;
    },
    chartData() {
      return {
        labels: this.rows.map((r) => r.title),
        datasets: [{
          label: this.mode === 'pawer' ? 'Usos' : 'Atenciones',
          data: this.rows.map((r) => r[this.countKey]),
          backgroundColor: COLORS.primary,
          maxBarThickness: 18,
        }],
      };
    },
    chartOptions() {
      const rows = this.rows;
      const unit = this.mode === 'pawer' ? 'usos' : 'atenciones';
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            ticks: { beginAtZero: true, precision: 0, fontColor: COLORS.textMuted },
            gridLines: { color: COLORS.divider },
          }],
          yAxes: [{
            ticks: {
              fontColor: COLORS.textMuted,
              callback: (label) => (label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label),
            },
            gridLines: { display: false },
          }],
        },
        tooltips: {
          callbacks: {
            title: (items) => rows[items[0].index].title,
            label: (item) => {
              const row = rows[item.index];
              return `${row[this.countKey]} ${unit} (${row.percentage.toFixed(1)}%)`;
            },
            afterLabel: (item) =>
              this.showCosts ? `Cobertura Pawer: ${clpFormatter.format(rows[item.index].pawerCoverage || 0)}` : '',
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.header {
  gap: 8px;
}
.state-msg {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.chart-container {
  position: relative;
}
</style>
